import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import firebase from "firebase";
import axios from "axios";
import styled from "styled-components";
import ActivityPage, { Heading, Muted, PrimaryButton } from "./ActivityPage";
import FirebaseAuth from "../../shared/FirebaseAuth/FirebaseAuth";
import { getAppFunctionsUrl } from "../../../helpers/firebase/appFirebase";

const Details = styled.div`
	text-align: left;
	margin-bottom: 24px;

	p {
		margin: 0 0 8px;
	}
`;

const DangerButton = styled(PrimaryButton)`
	background-color: #d0021b;
	border-color: #d0021b;

	&:hover,
	&:focus,
	&:active {
		background-color: #a80116;
		border-color: #a80116;
	}
`;

interface AccountProps {
	app: firebase.app.App;
	onDeleted: () => void;
}

/**
 * Account details + delete button for the signed-in user. Rendered inside
 * `FirebaseAuth`, so there is always a signed-in user on the app's project.
 */
const Account: React.FC<AccountProps> = ({ app, onDeleted }) => {
	const [user, setUser] = useState<firebase.User | null>(app.auth().currentUser);
	const [error, setError] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		const unsubscribe = app.auth().onAuthStateChanged(setUser);
		return () => unsubscribe();
	}, [app]);

	const deleteUserAccount = async () => {
		if (!user) {
			setError("User not found. Please log in.");
			return;
		}

		setIsDeleting(true);
		try {
			const idToken = await user.getIdToken();
			await axios.delete(`${getAppFunctionsUrl(app)}/users/${user.uid}`, {
				params: { email: user.email },
				headers: { Authorization: `Bearer ${idToken}` },
			});
			onDeleted();
			await app.auth().signOut();
		} catch (reason) {
			setError("Could not delete account. Please try again.");
			setIsDeleting(false);
		}
	};

	const handleDeleteAccount = () => {
		if (window.confirm("Are you sure you want to delete this account? This cannot be undone.")) {
			deleteUserAccount();
		}
	};

	if (!user) {
		return <Muted>Loading…</Muted>;
	}

	return (
		<>
			<Details>
				<p>Name: {user.displayName}</p>
				<p>Email: {user.email}</p>
			</Details>
			{error && <div className="error m-bottom">{error}</div>}
			<DangerButton type="button" onClick={handleDeleteAccount} disabled={isDeleting}>
				{isDeleting ? "Deleting…" : "Delete account"}
			</DangerButton>
		</>
	);
};

/**
 * /activity/delete/:appKey
 *
 * Public account-deletion page linked from the app stores. Signs the user in
 * to the white-label app's Firebase project (via the shared `FirebaseAuth`
 * gate), then deletes the account through that app's functions API.
 */
const DeleteAccountActivity: React.FC = () => {
	const { appKey } = useParams<{ appKey: string }>();
	const [deleted, setDeleted] = useState(false);

	return (
		<ActivityPage title="Manage your account" appKey={appKey}>
			{app =>
				deleted ? (
					<>
						<Heading>Account deleted</Heading>
						<p style={{ margin: 0 }}>Your account has been deleted. You can close this page.</p>
					</>
				) : (
					<>
						<Heading>Manage your account</Heading>
						<FirebaseAuth firebaseApp={app}>
							<Account app={app} onDeleted={() => setDeleted(true)} />
						</FirebaseAuth>
					</>
				)
			}
		</ActivityPage>
	);
};

export default DeleteAccountActivity;
